import React, { useEffect, useState } from 'react';

import { X, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth, crmSync } from '@/contexts/AuthContext';
import { buildSlug, leadPriceFor } from '@/lib/marketplace';
import { submitForm } from '@/lib/crmForms';
import { useOverlayLock } from '@/lib/overlay';


import PhotoUploader from '@/components/PhotoUploader';
import DocumentUploader from '@/components/DocumentUploader';
import {
  DEFAULT_MEDIA,
  ListingDocument,
  MediaSettings,
  loadListingSettings,
  parseDocuments,
} from '@/lib/listingSettings';
import { BUDGET_RANGES, BUDGET_RANGES_ES, US_STATES, homeownerCategories, gcTrades, tradeLabel } from '@/data/constants';


const inputCls =
  'w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-slate-900 focus:outline-none';
const labelCls = 'mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500';

export interface EditableListing {
  id: string;
  title: string;
  description?: string;
  trade: string;
  city: string;
  state: string;
  zip?: string;
  budget_range?: string;
  target_date?: string;
  project_type: string;
  notes?: string;
  photos?: string[] | null;
  documents?: unknown;
}

interface Props {
  mode: 'project' | 'workorder';
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  /** When provided the form edits this listing instead of creating a new one. */
  listing?: EditableListing | null;
}

const ListingForm: React.FC<Props> = ({ mode, open, onClose, onCreated, listing }) => {
  const { t, lang } = useI18n();
  const { profile } = useAuth();
  const [f, setF] = useState<Record<string, string>>({ project_type: mode === 'workorder' ? 'commercial' : 'residential' });
  const [photos, setPhotos] = useState<string[]>([]);
  const [documents, setDocuments] = useState<ListingDocument[]>([]);
  const [media, setMedia] = useState<MediaSettings>(DEFAULT_MEDIA);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const editing = !!listing;

  // Tell the floating Start Now / WhatsApp stack to step aside while this
  // full-screen form owns the viewport (and lock the page behind it).
  useOverlayLock(open);

  // Admin-configured upload limits (images, PDFs, sizes, MIME types).

  useEffect(() => {
    if (!open) return;
    loadListingSettings().then((s) => setMedia(s.media));
  }, [open]);


  // Load the selected listing into the form (or reset for a new post).
  useEffect(() => {
    if (!open) return;
    setError('');
    setDone(false);
    if (listing) {
      setF({
        title: listing.title || '',
        description: listing.description || '',
        trade: listing.trade || '',
        city: listing.city || '',
        state: listing.state || '',
        zip: listing.zip || '',
        budget_range: listing.budget_range || '',
        target_date: listing.target_date || '',
        project_type: listing.project_type || (mode === 'workorder' ? 'commercial' : 'residential'),
        notes: listing.notes || '',
      });
      setPhotos((listing.photos || []).filter(Boolean));
      setDocuments(parseDocuments(listing.documents));
    } else {
      setF({ project_type: mode === 'workorder' ? 'commercial' : 'residential' });
      setPhotos([]);
      setDocuments([]);
    }

  }, [open, listing, mode]);

  const set = (k: string, v: string) => setF((p) => ({ ...p, [k]: v }));
  const options = mode === 'workorder' ? gcTrades() : homeownerCategories();
  const budgets = lang === 'es' ? BUDGET_RANGES_ES : BUDGET_RANGES;

  if (!open) return null;

  /**
   * Turn a database / PostgREST error into something a contractor can act on.
   *
   * The posting trigger raises machine codes (documents_must_be_array,
   * too_many_documents, document_path_invalid...). A live customer was shown
   * the raw "documents must be array" string and had no idea what to do, so
   * every known code is mapped to plain bilingual copy here.
   */
  const friendlyError = (msg?: string | null): string => {
    const m = (msg || '').toLowerCase();
    if (m.includes('documents_must_be_array') || m.includes('documents must be array')) return t('form.errDocsRetry');
    if (m.includes('too_many_documents')) return t('form.errDocsMax');
    if (m.includes('document_too_large')) return t('form.errDocsSize');
    if (m.includes('document_path_invalid') || m.includes('document_not_owned') || m.includes('document_type_invalid'))
      return t('form.errDocsInvalid');
    if (m.includes('too_many_images')) return t('form.errPhotosMax');
    return msg || t('pay.error');
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!f.title || !f.description || !f.trade || !f.city || !f.state) return setError(t('auth.errInvalid'));
    if (!profile) return;
    setBusy(true);

    const price = await leadPriceFor(f.trade, f.project_type || 'residential');

    // `documents` is ALWAYS sent as a JSON array — empty array when the poster
    // attached nothing. parseDocuments() also strips anything that is not a
    // plain {path,name,size,kind,type} object, so a half-finished upload can
    // never turn the column into a string/object and block publishing.
    const docs: ListingDocument[] = parseDocuments(documents);
    const safePhotos: string[] = (photos || []).filter((p) => typeof p === 'string' && p.length > 0);

    // ── EDIT ──────────────────────────────────────────────────────────────
    if (editing && listing) {
      const { error: upErr } = await supabase
        .from('mkt_listings')
        .update({
          title: f.title,
          description: f.description,
          trade: f.trade,
          city: f.city,
          state: f.state,
          zip: f.zip || null,
          budget_range: f.budget_range || null,
          target_date: f.target_date || null,
          project_type: f.project_type || 'residential',
          photos: safePhotos,
          documents: docs,

          notes: f.notes || null,
          lead_price: price,
        })
        .eq('id', listing.id)
        .eq('owner_id', profile.id);

      setBusy(false);
      if (upErr) return setError(friendlyError(upErr.message));

      await supabase.from('mkt_activity').insert({
        user_id: profile.id,
        action: mode === 'workorder' ? 'work_order_updated' : 'project_updated',
        detail: `${f.trade} · ${f.city}, ${f.state} · ${safePhotos.length} photos · ${docs.length} docs`,
      });
      setDone(true);
      onCreated();
      return;
    }

    // ── CREATE ────────────────────────────────────────────────────────────
    const slug = buildSlug(f.trade, f.city, f.state);

    const row = {
      owner_id: profile.id,
      source_type: mode === 'workorder' ? 'gc' : 'homeowner',
      slug,
      title: f.title,
      description: f.description,
      trade: f.trade,
      city: f.city,
      state: f.state,
      zip: f.zip || null,
      budget_range: f.budget_range || null,
      target_date: f.target_date || null,
      project_type: f.project_type || 'residential',
      photos: safePhotos,
      documents: docs,

      notes: f.notes || null,
      status: 'published',
      lead_price: price,
      language: lang,
    };

    let { data, error: err } = await supabase.from('mkt_listings').insert(row).select('id').maybeSingle();

    // Last-resort safety net: if the attachment payload is ever rejected, the
    // project itself must still publish. Retry once with no attachments and
    // tell the poster their files need re-uploading — never lose the project.
    if (err && /documents/i.test(err.message || '')) {
      const retry = await supabase
        .from('mkt_listings')
        .insert({ ...row, documents: [] })
        .select('id')
        .maybeSingle();
      if (!retry.error && retry.data) {
        data = retry.data;
        err = null;
        setError(t('form.errDocsDropped'));
      }
    }

    if (err || !data) {
      setBusy(false);
      setError(friendlyError(err?.message));
      return;
    }


    await supabase.from('mkt_listing_contacts').insert({
      listing_id: data.id,
      contact_name:
        profile.company_name || `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.email,
      phone: profile.phone,
      email: profile.email,
      address: f.address || `${f.city}, ${f.state} ${f.zip || ''}`,
    });

    await supabase.from('mkt_activity').insert({
      user_id: profile.id,
      action: mode === 'workorder' ? 'work_order_created' : 'project_created',
      detail: `${f.trade} · ${f.city}, ${f.state}`,
    });

    await crmSync({
      event: mode === 'workorder' ? 'work_order_created' : 'project_created',
      email: profile.email,
      name: profile.company_name || profile.first_name || profile.email,
      phone: profile.phone,
      language: lang,
      userType: profile.user_type,
      details: { Title: f.title, Trade: f.trade, Location: `${f.city}, ${f.state}` },
    });

    // Also push the full submission through the shared form pipeline so the
    // GoHighLevel workflow / pipeline stage for posted work fires.
    await submitForm({
      formType: mode === 'workorder' ? 'work-order-posted' : 'project-posted',
      email: profile.email,
      name: profile.company_name || `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.email,
      phone: profile.phone || undefined,
      language: lang,
      userType: profile.user_type,
      sendEmail: false,
      fields: {
        title: f.title,
        trade: f.trade,
        city: f.city,
        state: f.state,
        zip: f.zip,
        project_type: f.project_type || 'residential',
        budget_range: f.budget_range,
        target_date: f.target_date,
        lead_price: price,
      },
    });

    setBusy(false);
    setDone(true);
    onCreated();
  };


  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-slate-900/60 sm:items-center sm:p-4">
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-2xl bg-white p-6 shadow-2xl sm:rounded-2xl">
        <div className="flex items-start justify-between">
          <h2 className="text-xl font-extrabold text-slate-900">
            {editing
              ? mode === 'workorder'
                ? t('form.editWorkOrder')
                : t('form.editProject')
              : mode === 'workorder'
              ? t('dash.postWorkOrder')
              : t('dash.postProject')}
          </h2>
          <button onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100" aria-label={t('common.close')}>

            <X className="h-5 w-5" />
          </button>
        </div>

        {done ? (
          <div className="py-10 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
            <p className="mt-4 font-semibold text-slate-800">
              {mode === 'workorder' ? t('form.successWo') : t('form.successProject')}
            </p>
            <button onClick={onClose} className="mt-6 rounded-xl bg-slate-900 px-6 py-3 text-sm font-bold text-white">
              {t('common.close')}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-5 grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className={labelCls}>{mode === 'workorder' ? t('form.woTitle') : t('form.projectTitle')}</label>
              <input className={inputCls} value={f.title || ''} onChange={(e) => set('title', e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>{mode === 'workorder' ? t('form.woDesc') : t('form.projectDesc')}</label>
              <textarea rows={4} className={inputCls} value={f.description || ''} onChange={(e) => set('description', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>{mode === 'workorder' ? t('form.tradeRequired') : t('form.category')}</label>
              <select className={inputCls} value={f.trade || ''} onChange={(e) => set('trade', e.target.value)}>
                <option value="">—</option>
                {options.map((tr) => (
                  <option key={tr.slug} value={tr.slug}>{tradeLabel(tr.slug, lang)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>{t('form.projectType')}</label>
              <select className={inputCls} value={f.project_type} onChange={(e) => set('project_type', e.target.value)}>
                <option value="residential">{t('common.residential')}</option>
                <option value="commercial">{t('common.commercial')}</option>
              </select>
            </div>
            {mode === 'project' && (
              <div className="sm:col-span-2">
                <label className={labelCls}>{t('form.address')}</label>
                <input className={inputCls} value={f.address || ''} onChange={(e) => set('address', e.target.value)} />
              </div>
            )}
            <div><label className={labelCls}>{t('common.city')}</label><input className={inputCls} value={f.city || ''} onChange={(e) => set('city', e.target.value)} /></div>
            <div>
              <label className={labelCls}>{t('common.state')}</label>
              <select className={inputCls} value={f.state || ''} onChange={(e) => set('state', e.target.value)}>
                <option value="">—</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div><label className={labelCls}>{t('common.zip')}</label><input className={inputCls} value={f.zip || ''} onChange={(e) => set('zip', e.target.value)} /></div>
            <div>
              <label className={labelCls}>{mode === 'workorder' ? t('form.budget') : t('form.budgetRange')}</label>
              <select className={inputCls} value={f.budget_range || ''} onChange={(e) => set('budget_range', e.target.value)}>
                <option value="">—</option>
                {budgets.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>{mode === 'workorder' ? t('form.dueDate') : t('form.startDate')}</label>
              <input type="date" className={inputCls} value={f.target_date || ''} onChange={(e) => set('target_date', e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>{t('form.photos')}</label>
              <PhotoUploader
                value={photos}
                onChange={setPhotos}
                max={media.max_images}
                maxMb={media.max_image_mb}
                accept={media.image_types}
              />
              <p className="mt-2 text-xs text-slate-400">{t('form.photosHint')}</p>
            </div>

            <div className="sm:col-span-2">
              <label className={labelCls}>{t('form.documents')}</label>
              <DocumentUploader
                value={documents}
                onChange={setDocuments}
                max={media.max_documents}
                maxMb={media.max_document_mb}
                accept={media.document_types}
              />
              <p className="mt-2 text-xs text-slate-400">{t('form.documentsHint')}</p>
            </div>


            <div className="sm:col-span-2">
              <label className={labelCls}>{t('form.notes')}</label>
              <textarea rows={2} className={inputCls} value={f.notes || ''} onChange={(e) => set('notes', e.target.value)} />
            </div>
            {error && <p className="sm:col-span-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
            <div className="sm:col-span-2">
              <button type="submit" disabled={busy} className="w-full rounded-xl bg-orange-500 px-4 py-3.5 text-base font-bold text-white hover:bg-orange-600 disabled:opacity-50">
                {busy ? t('form.publishing') : editing ? t('form.saveChanges') : t('form.publish')}
              </button>
            </div>

          </form>
        )}
      </div>
    </div>
  );
};

export default ListingForm;
